import { useState } from "react";
import { ArrowRight, UploadCloud, Send } from "lucide-react";
import { toast } from "sonner";
import { ChapterHeading, Reveal } from "./Reveal";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ROLES = [
  {
    tag: "Clinical Leadership",
    title: "BCBA",
    text: "Assessment, treatment planning, supervision, caregiver collaboration, and clinical leadership.",
  },
  {
    tag: "Direct Care",
    title: "RBT",
    text: "Provide compassionate direct ABA services under BCBA supervision.",
  },
  {
    tag: "Behavior Analysis",
    title: "BCaBA",
    text: "Work collaboratively within your credentialing and supervision requirements.",
  },
];

export const Jobs = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "BCBA", message: "" });
  const [resume, setResume] = useState(null);

  const apply = (role) => {
    setForm((f) => ({ ...f, role }));
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!resume) {
      toast.error("Please attach your resume (PDF, DOC, or DOCX).");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("resume", resume);
      await api.post("/applications", fd);
      toast.success("Application submitted. Thank you for your interest!");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", role: form.role, message: "" });
      setResume(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="jobs" data-testid="jobs-section" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <ChapterHeading
          number="06"
          eyebrow="Jobs"
          title="Join Milestone"
          accent="Pediatric Therapy."
          testid="jobs-heading"
        />

        <Reveal>
          <p className="text-base md:text-lg leading-relaxed text-brand-muted max-w-2xl mb-14">
            We are building a team of compassionate professionals who value ethical care,
            collaboration, clinical quality, and meaningful outcomes.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-8" data-testid="jobs-grid">
          {ROLES.map((r, i) => (
            <Reveal key={r.title} delay={0.08 * i}>
              <article
                data-testid={`job-card-${r.title.toLowerCase()}`}
                className="group h-full flex flex-col rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-soft p-8 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-2 hover:shadow-lift hover:border-brand-coral/40"
              >
                <span className="self-start rounded-full bg-brand-pale text-brand-blue text-xs font-semibold uppercase tracking-[0.16em] px-4 py-1.5 mb-6">
                  {r.tag}
                </span>
                <h3 className="font-serif text-4xl font-semibold text-brand-blue mb-4">{r.title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-brand-muted flex-1">{r.text}</p>
                <button
                  data-testid={`job-apply-${r.title.toLowerCase()}`}
                  onClick={() => apply(r.title)}
                  className="mt-8 inline-flex items-center gap-2 text-brand-coral font-semibold transition-colors duration-300 hover:text-[#e05f5f]"
                >
                  Express Interest
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                </button>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="application-dialog" className="sm:max-w-xl rounded-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-brand-blue">Express Interest — {form.role}</DialogTitle>
            <DialogDescription>
              Tell us a little about yourself and attach your resume. We'll reach out as we build our team.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5 mt-2" data-testid="application-form">
            <div className="space-y-2">
              <Label htmlFor="app-name">Full Name</Label>
              <Input id="app-name" data-testid="application-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-email">Email</Label>
              <Input id="app-email" data-testid="application-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="app-phone">Phone</Label>
              <Input id="app-phone" data-testid="application-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12" />
            </div>
            <div className="space-y-2">
              <Label>Role of Interest</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="application-role" className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50 h-12">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BCBA">BCBA</SelectItem>
                  <SelectItem value="RBT">RBT</SelectItem>
                  <SelectItem value="BCaBA">BCaBA</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="app-message">Message</Label>
              <Textarea id="app-message" data-testid="application-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl bg-brand-pale/50 border-brand-lightblue/50" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="app-resume">Resume (PDF, DOC, or DOCX — max 10 MB)</Label>
              <label
                htmlFor="app-resume"
                className="flex items-center gap-3 rounded-xl border-2 border-dashed border-brand-lightblue/70 bg-brand-pale/40 px-5 py-5 cursor-pointer transition-colors duration-300 hover:border-brand-coral/60 hover:bg-brand-coralpale/40"
              >
                <UploadCloud className="h-6 w-6 text-brand-blue shrink-0" />
                <span className="text-sm text-brand-muted" data-testid="application-resume-label">
                  {resume ? resume.name : "Click to attach your resume"}
                </span>
                <input
                  id="app-resume"
                  data-testid="application-resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => setResume(e.target.files?.[0] || null)}
                />
              </label>
            </div>
            <button
              type="submit"
              data-testid="application-submit-button"
              disabled={sending}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-10 py-4 shadow-soft transition-[transform,box-shadow,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:shadow-lift hover:bg-[#f06666] disabled:opacity-60 disabled:translate-y-0"
            >
              {sending ? "Submitting…" : "Submit Application"}
              <Send className="h-4 w-4" />
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
